import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Modal } from "@/components/Modal";
import type { Recipe } from "@/lib/schema";
import { recipeFinishedGrams, recipePortions } from "./amount";
import { formatAmount } from "./metrics";
import { RecipeEditorModal } from "./RecipeEditorModal";
import { sumMetrics } from "./scoring";

// Oppskrifter: the recipe list. Contents, name, finished weight and portions
// are all changed in the editor (docs/adr/0006, superseding 0005's
// re-save-only rule); this modal creates, opens and deletes.
interface Props {
  open: boolean;
  initial: Recipe[];
  foodClarifications?: string[];
  onSave: (recipes: Recipe[]) => void;
  onClose: () => void;
}

export function RecipesModal({ open, ...rest }: Props) {
  if (!open) return null;
  return <ModalBody {...rest} />;
}

function ModalBody({
  initial,
  foodClarifications,
  onSave,
  onClose,
}: Omit<Props, "open">) {
  const [draft, setDraft] = useState<Recipe[]>(initial);
  // null = closed; { recipe: null } = creating a new one.
  const [editing, setEditing] = useState<{ recipe: Recipe | null } | null>(
    null,
  );

  const remove = (id: string) => {
    setDraft((prev) => prev.filter((r) => r.id !== id));
  };

  const saveFromEditor = (recipe: Recipe) => {
    setDraft((prev) =>
      prev.some((r) => r.id === recipe.id)
        ? prev.map((r) => (r.id === recipe.id ? recipe : r))
        : [...prev, recipe],
    );
    setEditing(null);
  };

  return (
    <Modal open onClose={onClose} data-testid="recipes-modal" size="lg">
      <h2>Oppskrifter</h2>
      <p className="hint">
        Gjenbrukbare ingredienslister som legges til i måltider uten KI. Endrer
        du en oppskrift her, beholder måltider som allerede er logget sin egen
        kopi.
      </p>

      <button
        type="button"
        className="btn ghost icon-btn"
        onClick={() => setEditing({ recipe: null })}
        data-testid="new-recipe-button"
      >
        <Plus size={14} />
        Ny oppskrift
      </button>

      {draft.length > 0 ? (
        <div className="task-list">
          {draft.map((recipe) => {
            const portions = recipePortions(recipe);
            return (
              <div
                key={recipe.id}
                className="task-row recipe-row"
                data-testid={`recipe-row-${recipe.id}`}
              >
                <span
                  className="recipe-name"
                  data-testid={`recipe-name-${recipe.id}`}
                >
                  {recipe.name}
                </span>
                <span className="hint recipe-summary">
                  {recipe.ingredients.length}{" "}
                  {recipe.ingredients.length === 1
                    ? "ingrediens"
                    : "ingredienser"}
                  , {formatAmount(recipeFinishedGrams(recipe))} g
                  {portions > 1 ? `, ${portions} porsjoner` : ""},{" "}
                  {formatAmount(sumMetrics(recipe.ingredients).kcal)} kcal
                </span>
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => setEditing({ recipe })}
                  aria-label={`Rediger oppskrift ${recipe.name}`}
                  data-testid={`edit-recipe-button-${recipe.id}`}
                >
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  className="icon-btn-delete"
                  onClick={() => remove(recipe.id)}
                  aria-label={`Slett oppskrift ${recipe.name}`}
                  data-testid={`delete-recipe-button-${recipe.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="hint" data-testid="recipes-empty">
          Ingen oppskrifter ennå.
        </p>
      )}

      <div className="modal-actions">
        <button
          type="button"
          className="btn ghost"
          onClick={onClose}
          data-testid="recipes-cancel-button"
        >
          Avbryt
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => onSave(draft)}
          data-testid="save-recipes-button"
        >
          Lagre
        </button>
      </div>

      <RecipeEditorModal
        open={editing !== null}
        initial={editing?.recipe ?? null}
        recipes={draft}
        foodClarifications={foodClarifications}
        onSave={saveFromEditor}
        onClose={() => setEditing(null)}
      />
    </Modal>
  );
}
